/**
 * Elimination Style1 — Premium section renderers for GridEngine
 * High-energy design with dramatic reds/oranges for elimination theme
 * 
 * Exports:
 * - EliminationStyle1HeroSection: Premium animated hero with glowing orbs, floating icons, "ELIMINATION IN PROGRESS" live badge
 * - EliminationStyle1HowItWorksSection: 4 emoji steps + 3-rule grid + prize breakdown
 * - EliminationStyle1FinalCTASection: Dark gradient urgency CTA with remaining tickets
 */

import React from 'react'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Target from 'lucide-react/dist/esm/icons/target.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import EliminationTracker from '../../../components/EliminationTracker'
import { formatCurrencyValue } from '../../../utils/formatCurrency'
import { useTranslations } from '../../../contexts/TranslationContext'

/**
 * Hero Section — Premium animated hero with glowing orbs (red/orange/yellow), 
 * floating Target/Zap/Award icons, "ELIMINATION IN PROGRESS" live badge
 */
export function EliminationStyle1HeroSection({
  mainDraw,
  editMode,
  handleTranslationUpdate,
  onLoginClick, language,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}

  const totalTickets = mainDraw?.total_tickets ?? 0
  const remainingTickets = mainDraw?.remaining_tickets ?? 0
  const grandPrize = mainDraw?.grand_prize ?? mainDraw?.current_jackpot ?? 0
  
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name

  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, var(--charity-gradient-start, #111827) 0%, var(--charity-gradient-end, #7f1d1d) 60%, rgba(124,45,18,0.95) 100%)'
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-yellow-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Floating target icons */}
        <div className="absolute top-20 left-[10%] opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Target className="w-16 h-16 text-red-400" />
        </div>
        <div className="absolute top-32 right-[15%] opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
          <Zap className="w-12 h-12 text-orange-400" />
        </div>
        <div className="absolute bottom-32 left-[20%] opacity-20 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1s' }}>
          <Award className="w-14 h-14 text-yellow-400" />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <HeroSiteLogo tone="dark" />
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="text-center lg:text-left">
            {/* Prominent Charity Logo */}
            {(() => {
              const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
              const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
              const logoUrl = isDefaultLogo ? brandLogo : (branding?.logoUrl || brandLogo)
              const logoAlt = isDefaultLogo ? brandName : (branding?.charityName || brandName || tr('common.charityPartner', 'Charity Partner'))
              
              return logoUrl ? (
                <div className="mb-8 flex justify-center lg:justify-start">
                  <div className="relative group">
                    <div className="absolute -inset-3 bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
                    <div className="relative bg-white rounded-2xl px-6 py-4 shadow-2xl">
                      <img 
                        src={logoUrl} 
                        alt={logoAlt}
                        className="h-16 sm:h-20 lg:h-24 w-auto object-contain max-w-[280px]"
                      />
                    </div>
                  </div>
                </div>
              ) : null
            })()}

            {/* Live Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 backdrop-blur-sm border border-red-500/30 text-red-400 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <InlineTranslationEdit
                translationKey="elimination.inProgress"
                value={c('elimination.inProgress', 'ELIMINATION IN PROGRESS')}
                fallback="ELIMINATION IN PROGRESS"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-4 leading-tight">
              <InlineTranslationEdit
                translationKey="elimination.heroTitle"
                value={c('elimination.heroTitle', 'LAST TICKET WINS')}
                fallback="LAST TICKET WINS"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-orange-200/80 mb-8">
              {c('elimination.remainingTickets', tr('elimination.remainingTickets', 'Only {count} tickets remain.'))
                .split('{count}').map((part, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <span className="text-orange-400 font-bold">{remainingTickets.toLocaleString()}</span>}
                    {part}
                  </React.Fragment>
                ))}{' '}
              <span className="text-yellow-400 font-bold">
                {' '}<InlineTranslationEdit
                  translationKey="elimination.tagline"
                  value={c('elimination.tagline', 'Survive to win!')}
                  fallback="Survive to win!"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
            </p>

            {/* Prize Display - Mobile */}
            <div className="lg:hidden mb-8">
              <div className="bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500 rounded-3xl p-6 shadow-2xl shadow-orange-500/30">
                <p className="text-orange-100 font-bold uppercase text-sm tracking-wider mb-2">
                  <InlineTranslationEdit
                    translationKey="elimination.grandPrizeLabel"
                    value={c('elimination.grandPrizeLabel', 'Grand Prize')}
                    fallback="Grand Prize"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-5xl md:text-6xl font-black text-white">
                  {formatCurrencyValue(grandPrize, { language })}
                </p>
              </div>
            </div>

            <HeroPartnerGlassSelect className="mx-auto lg:mx-0 mb-8 w-full max-w-md" />

            {/* CTA Button */}
            <button
              onClick={() => purchaseNavigate('/purchase')}
              className="group relative w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-xl font-black rounded-2xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/40"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <Ticket className="w-6 h-6" />
                <InlineTranslationEdit
                  translationKey="elimination.buyTickets"
                  value={c('elimination.buyTickets', 'Get Your Ticket')}
                  fallback="Get Your Ticket"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>

          {/* Right Column - Prize Display (Desktop Only) */}
          <div className="hidden lg:flex items-center justify-center">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500 rounded-3xl blur-3xl opacity-40 animate-pulse" />
              
              {/* Prize Card */}
              <div className="relative bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500 rounded-3xl p-12 shadow-2xl shadow-orange-500/30">
                <div className="text-center">
                  <Trophy className="w-20 h-20 mx-auto mb-6 text-white opacity-90" />
                  <p className="text-orange-100 font-bold uppercase text-sm tracking-wider mb-3">
                    <InlineTranslationEdit
                      translationKey="elimination.grandPrizeLabel"
                      value={c('elimination.grandPrizeLabel', 'Grand Prize')}
                      fallback="Grand Prize"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </p>
                  <p className="text-6xl md:text-7xl font-black text-white mb-4">
                    {formatCurrencyValue(grandPrize, { language })}
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-bold">
                    <Target className="w-4 h-4" />
                    <span>{tr('elimination.remainingCount', '{count} left').replace('{count}', remainingTickets.toLocaleString())}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * How It Works Section — 4 emoji steps + 3-rule grid + prize breakdown
 */
export function EliminationStyle1HowItWorksSection({
  mainDraw,
  editMode,
  handleTranslationUpdate, language,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()

  const steps = [
    {
      icon: '🎟️',
      step: '1',
      titleKey: 'elimination.step1Title',
      titleFallback: 'Buy Your Tickets',
      descKey: 'elimination.step1Desc',
      descFallback: 'Purchase tickets to enter the elimination game. Each ticket is a chance to win!',
    },
    {
      icon: '🎯',
      step: '2',
      titleKey: 'elimination.step2Title',
      titleFallback: 'Survive Eliminations',
      descKey: 'elimination.step2Desc',
      descFallback: 'Tickets are randomly eliminated. If yours survives, you stay in the game!',
    },
    {
      icon: '🏆',
      step: '3',
      titleKey: 'elimination.step3Title',
      titleFallback: 'Win Milestone Prizes',
      descKey: 'elimination.step3Desc',
      descFallback: 'As tickets are eliminated, remaining holders win milestone prizes.',
    },
    {
      icon: '👑',
      step: '4',
      titleKey: 'elimination.step4Title',
      titleFallback: 'Last Ticket Wins!',
      descKey: 'elimination.step4Desc',
      descFallback: 'The final surviving ticket wins the grand prize!',
    },
  ]

  const rules = [
    {
      Icon: Target,
      titleKey: 'elimination.rule1Title',
      titleFallback: 'Random Elimination',
      descKey: 'elimination.rule1Desc',
      descFallback: 'Tickets are eliminated randomly at each draw. Fair and transparent!',
    },
    {
      Icon: Award,
      titleKey: 'elimination.rule2Title',
      titleFallback: 'Milestone Rewards',
      descKey: 'elimination.rule2Desc',
      descFallback: 'Win prizes at key milestones: 1000, 500, 100, 10, and the last ticket!',
    },
    {
      Icon: Users,
      titleKey: 'elimination.rule3Title',
      titleFallback: 'Better Odds',
      descKey: 'elimination.rule3Desc',
      descFallback: 'As tickets are eliminated, your odds of winning improve every round!',
    },
  ]

  const milestones = mainDraw?.milestones || []

  return (
    <section className="py-12 bg-white dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            <InlineTranslationEdit
              translationKey="elimination.howItWorksTitle"
              value={c('elimination.howItWorksTitle', 'How Elimination Works')}
              fallback="How Elimination Works"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            <InlineTranslationEdit
              translationKey="elimination.howItWorksDesc"
              value={c('elimination.howItWorksDesc', 'The ultimate survival game — will your ticket be the last one standing?')}
              fallback="The ultimate survival game — will your ticket be the last one standing?"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {steps.map((item, i) => (
            <div key={i} className="relative text-center">
              {i < 3 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-orange-300 to-transparent dark:from-orange-500/30" />
              )}
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-2xl flex items-center justify-center text-3xl shadow-md border border-orange-200 dark:border-orange-500/30">
                  {item.icon}
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow">
                  {item.step}
                </div>
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                <InlineTranslationEdit
                  translationKey={item.titleKey}
                  value={c(item.titleKey, item.titleFallback)}
                  fallback={item.titleFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit
                  translationKey={item.descKey}
                  value={c(item.descKey, item.descFallback)}
                  fallback={item.descFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          ))}
        </div>

        {/* Rules */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {rules.map((rule, i) => (
            <div
              key={i}
              className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
            >
              <rule.Icon className="w-10 h-10 text-orange-500 mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                <InlineTranslationEdit
                  translationKey={rule.titleKey}
                  value={c(rule.titleKey, rule.titleFallback)}
                  fallback={rule.titleFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <InlineTranslationEdit
                  translationKey={rule.descKey}
                  value={c(rule.descKey, rule.descFallback)}
                  fallback={rule.descFallback}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          ))}
        </div>

        {/* Prize Breakdown */}
        {milestones.length > 0 && (
          <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/10 rounded-2xl p-6 border border-orange-200 dark:border-orange-500/30">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 text-center flex items-center justify-center gap-2">
              <Star className="w-5 h-5 text-orange-500" />
              <InlineTranslationEdit
                translationKey="elimination.prizeStructure"
                value={c('elimination.prizeStructure', 'Prize Structure')}
                fallback="Prize Structure"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {milestones.slice(0, 4).map((milestone, i) => (
                <div
                  key={i}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-red-200 dark:border-red-500/20 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-red-500 via-orange-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {milestone.tickets || milestone.threshold || i + 1}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {milestone.label || tr('elimination.milestone', 'Milestone {count}').replace('{count}', i + 1)}
                  </p>
                  <p className="text-2xl font-black text-orange-600 dark:text-orange-400">
                    {formatCurrencyValue((milestone.prize || 0), { language })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Final CTA Section — Dark gradient urgency CTA with remaining tickets
 */
export function EliminationStyle1FinalCTASection({
  mainDraw,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
}) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const remainingTickets = mainDraw?.remaining_tickets ?? 0

  return (
    <section className="py-16 bg-gradient-to-br from-gray-900 via-red-900 to-orange-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500 to-orange-500 animate-pulse" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/30 backdrop-blur-sm border border-red-500/50 text-red-300 rounded-full text-sm font-bold mb-6">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <InlineTranslationEdit
            translationKey="elimination.urgency"
            value={c('elimination.urgency', 'TIME IS RUNNING OUT')}
            fallback="TIME IS RUNNING OUT"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>

        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
          <InlineTranslationEdit
            translationKey="elimination.finalCTATitle"
            value={c('elimination.finalCTATitle', 'Only {count} Tickets Remain!')}
            fallback="Only {count} Tickets Remain!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
        <p className="text-xl text-orange-200 mb-8">
          <InlineTranslationEdit
            translationKey="elimination.finalCTADesc"
            value={c('elimination.finalCTADesc', 'Secure your entry now before tickets run out. The fewer tickets remaining, the better your odds!')}
            fallback="Secure your entry now before tickets run out. The fewer tickets remaining, the better your odds!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>

        <div className="flex items-center justify-center gap-3 mb-8">
          <Target className="w-6 h-6 text-red-400" />
          <span className="text-4xl font-black text-white">{remainingTickets.toLocaleString()}</span>
          <span className="text-lg text-orange-200">
            <InlineTranslationEdit
              translationKey="elimination.ticketsLeft"
              value={c('elimination.ticketsLeft', 'tickets left')}
              fallback="tickets left"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </span>
        </div>

        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="group relative px-12 py-6 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white text-xl font-black rounded-2xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/40"
        >
          <span className="relative z-10 flex items-center justify-center gap-3">
            <Ticket className="w-6 h-6" />
            <InlineTranslationEdit
              translationKey="elimination.getTickets"
              value={c('elimination.getTickets', 'Get Tickets')}
              fallback="Get Tickets"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>
    </section>
  )
}
